<script setup>
import { ref, onMounted, computed } from 'vue'; // Import 'computed'
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const assets = ref([]);
// Use column names from dbo.Asset schema for form fields
const newAsset = ref({
  asset_no: '',
  asset_desc: '',
  loc_no: '', // FK
  dep_no: '', // FK
  category: '',
  status: '',
  criticality: '',
  auth_emp: '',
  Tech_notes: '', // ntext
  manf: '',
  model_no: '',
  serial_no: '',
  contractor: '',
  waranty: false, // bit - use boolean
  waranty_expire_date: null, // date
  waranty_notes: '', // ntext
  asset_notes: '', // ntext
  supplier: '',
  pur_price: null, // float
  date_aquired: null, // date
  current_value: null, // float
  Date_disposed: null, // date
  Next_Sch: null, // date
  bom_no: '',
  SiteCode: '' // FK
});
const editingAsset = ref(null); // Holds the asset being edited

// Added selectedAsset ref to track the row clicked in the table for Edit/Delete
const selectedAsset = ref(null);

// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!

const assetsApiUrl = `${API_BASE_URL}/assets`;
const locationsApiUrl = `${API_BASE_URL}/locations`; // Need to fetch locations for dropdown
const departmentsApiUrl = `${API_BASE_URL}/departments`; // Need to fetch departments for dropdown
const sitesApiUrl = `${API_BASE_URL}/sites`; // Need to fetch sites for dropdown

const locations = ref([]); // To hold the list of locations for the dropdown
const departments = ref([]); // To hold the list of departments for the dropdown
const sites = ref([]); // To hold the list of sites for the dropdown

const loading = ref(false); // Add loading state
const error = ref(null); // Add error state
const notification = ref({ // Add notification state
  show: false,
  message: '',
  type: '' // 'success', 'error', 'warning'
});

// --- Computed Properties for v-model ---
// This computed property handles the Asset Number input (asset_no)
const currentAssetNo = computed({
  get() {
    return editingAsset.value ? editingAsset.value.asset_no : newAsset.value.asset_no;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.asset_no = value;
    } else {
      newAsset.value.asset_no = value;
    }
  }
});

// This computed property handles the Asset Description input (asset_desc)
const currentAssetDesc = computed({
  get() {
    return editingAsset.value ? editingAsset.value.asset_desc : newAsset.value.asset_desc;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.asset_desc = value;
    } else {
      newAsset.value.asset_desc = value;
    }
  }
});

// This computed property handles the Location Number dropdown (loc_no)
const currentLocNo = computed({
  get() {
    return editingAsset.value ? editingAsset.value.loc_no : newAsset.value.loc_no;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.loc_no = value;
    } else {
      newAsset.value.loc_no = value;
    }
  }
});

// This computed property handles the Department Number dropdown (dep_no)
const currentDepNo = computed({
  get() {
    return editingAsset.value ? editingAsset.value.dep_no : newAsset.value.dep_no;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.dep_no = value;
    } else {
      newAsset.value.dep_no = value;
    }
  }
});

// This computed property handles the Category input
const currentCategory = computed({
  get() {
    return editingAsset.value ? editingAsset.value.category : newAsset.value.category;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.category = value;
    } else {
      newAsset.value.category = value;
    }
  }
});

// This computed property handles the Status input
const currentStatus = computed({
  get() {
    return editingAsset.value ? editingAsset.value.status : newAsset.value.status;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.status = value;
    } else {
      newAsset.value.status = value;
    }
  }
});

// This computed property handles the Criticality input
const currentCriticality = computed({
  get() {
    return editingAsset.value ? editingAsset.value.criticality : newAsset.value.criticality;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.criticality = value;
    } else {
      newAsset.value.criticality = value;
    }
  }
});

// This computed property handles the Authorized Employee input (auth_emp)
const currentAuthEmp = computed({
  get() {
    return editingAsset.value ? editingAsset.value.auth_emp : newAsset.value.auth_emp;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.auth_emp = value;
    } else {
      newAsset.value.auth_emp = value;
    }
  }
});

// This computed property handles the Technical Notes input (Tech_notes)
const currentTechNotes = computed({
  get() {
    return editingAsset.value ? editingAsset.value.Tech_notes : newAsset.value.Tech_notes;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.Tech_notes = value;
    } else {
      newAsset.value.Tech_notes = value;
    }
  }
});

// This computed property handles the Manufacturer input (manf)
const currentManf = computed({
  get() {
    return editingAsset.value ? editingAsset.value.manf : newAsset.value.manf;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.manf = value;
    } else {
      newAsset.value.manf = value;
    }
  }
});

// This computed property handles the Model Number input (model_no)
const currentModelNo = computed({
  get() {
    return editingAsset.value ? editingAsset.value.model_no : newAsset.value.model_no;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.model_no = value;
    } else {
      newAsset.value.model_no = value;
    }
  }
});

// This computed property handles the Serial Number input (serial_no)
const currentSerialNo = computed({
  get() {
    return editingAsset.value ? editingAsset.value.serial_no : newAsset.value.serial_no;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.serial_no = value;
    } else {
      newAsset.value.serial_no = value;
    }
  }
});

// This computed property handles the Contractor input
const currentContractor = computed({
  get() {
    return editingAsset.value ? editingAsset.value.contractor : newAsset.value.contractor;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.contractor = value;
    } else {
      newAsset.value.contractor = value;
    }
  }
});

// This computed property handles the Warranty checkbox (waranty)
const currentWaranty = computed({
  get() {
    return editingAsset.value ? editingAsset.value.waranty : newAsset.value.waranty;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.waranty = value;
    } else {
      newAsset.value.waranty = value;
    }
  }
});

// This computed property handles the Warranty Expire Date input (waranty_expire_date)
const currentWarantyExpireDate = computed({
  get() {
    // Format date for input type="date" (YYYY-MM-DD)
    const date = editingAsset.value ? editingAsset.value.waranty_expire_date : newAsset.value.waranty_expire_date;
    if (date) {
        const d = new Date(date);
        const month = ('0' + (d.getMonth() + 1)).slice(-2);
        const day = ('0' + d.getDate()).slice(-2);
        return `${d.getFullYear()}-${month}-${day}`;
    }
    return null;
  },
  set(value) {
    // Store as Date object or null
    const date = value ? new Date(value) : null;
    if (editingAsset.value) {
      editingAsset.value.waranty_expire_date = date;
    } else {
      newAsset.value.waranty_expire_date = date;
    }
  }
});

// This computed property handles the Warranty Notes input (waranty_notes)
const currentWarantyNotes = computed({
  get() {
    return editingAsset.value ? editingAsset.value.waranty_notes : newAsset.value.waranty_notes;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.waranty_notes = value;
    } else {
      newAsset.value.waranty_notes = value;
    }
  }
});

// This computed property handles the Asset Notes input (asset_notes)
const currentAssetNotes = computed({
  get() {
    return editingAsset.value ? editingAsset.value.asset_notes : newAsset.value.asset_notes;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.asset_notes = value;
    } else {
      newAsset.value.asset_notes = value;
    }
  }
});

// This computed property handles the Supplier input
const currentSupplier = computed({
  get() {
    return editingAsset.value ? editingAsset.value.supplier : newAsset.value.supplier;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.supplier = value;
    } else {
      newAsset.value.supplier = value;
    }
  }
});

// This computed property handles the Purchase Price input (pur_price)
const currentPurPrice = computed({
  get() {
    return editingAsset.value ? editingAsset.value.pur_price : newAsset.value.pur_price;
  },
  set(value) {
    // Convert to number, handle empty string or null
    const price = value === '' || value === null ? null : parseFloat(value);
    if (editingAsset.value) {
      editingAsset.value.pur_price = price;
    } else {
      newAsset.value.pur_price = price;
    }
  }
});

// This computed property handles the Date Acquired input (date_aquired)
const currentDateAquired = computed({
  get() {
    // Format date for input type="date" (YYYY-MM-DD)
    const date = editingAsset.value ? editingAsset.value.date_aquired : newAsset.value.date_aquired;
     if (date) {
        const d = new Date(date);
        const month = ('0' + (d.getMonth() + 1)).slice(-2);
        const day = ('0' + d.getDate()).slice(-2);
        return `${d.getFullYear()}-${month}-${day}`;
    }
    return null;
  },
  set(value) {
    // Store as Date object or null
    const date = value ? new Date(value) : null;
    if (editingAsset.value) {
      editingAsset.value.date_aquired = date;
    } else {
      newAsset.value.date_aquired = date;
    }
  }
});

// This computed property handles the Current Value input (current_value)
const currentCurrentValue = computed({
  get() {
    return editingAsset.value ? editingAsset.value.current_value : newAsset.value.current_value;
  },
  set(value) {
     // Convert to number, handle empty string or null
    const price = value === '' || value === null ? null : parseFloat(value);
    if (editingAsset.value) {
      editingAsset.value.current_value = price;
    } else {
      newAsset.value.current_value = price;
    }
  }
});

// This computed property handles the Date Disposed input (Date_disposed)
const currentDateDisposed = computed({
  get() {
    // Format date for input type="date" (YYYY-MM-DD)
    const date = editingAsset.value ? editingAsset.value.Date_disposed : newAsset.value.Date_disposed;
     if (date) {
        const d = new Date(date);
        const month = ('0' + (d.getMonth() + 1)).slice(-2);
        const day = ('0' + d.getDate()).slice(-2);
        return `${d.getFullYear()}-${month}-${day}`;
    }
    return null;
  },
  set(value) {
    // Store as Date object or null
    const date = value ? new Date(value) : null;
    if (editingAsset.value) {
      editingAsset.value.Date_disposed = date;
    } else {
      newAsset.value.Date_disposed = date;
    }
  }
});

// This computed property handles the Next Scheduled Maintenance input (Next_Sch)
const currentNextSch = computed({
  get() {
    // Format date for input type="date" (YYYY-MM-DD)
    const date = editingAsset.value ? editingAsset.value.Next_Sch : newAsset.value.Next_Sch;
     if (date) {
        const d = new Date(date);
        const month = ('0' + (d.getMonth() + 1)).slice(-2);
        const day = ('0' + d.getDate()).slice(-2);
        return `${d.getFullYear()}-${month}-${day}`;
    }
    return null;
  },
  set(value) {
    // Store as Date object or null
    const date = value ? new Date(value) : null;
    if (editingAsset.value) {
      editingAsset.value.Next_Sch = date;
    } else {
      newAsset.value.Next_Sch = date;
    }
  }
});

// This computed property handles the BOM Number input (bom_no)
const currentBomNo = computed({
  get() {
    return editingAsset.value ? editingAsset.value.bom_no : newAsset.value.bom_no;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.bom_no = value;
    } else {
      newAsset.value.bom_no = value;
    }
  }
});

// This computed property handles the Site Code dropdown (SiteCode)
const currentSiteCode = computed({
  get() {
    return editingAsset.value ? editingAsset.value.SiteCode : newAsset.value.SiteCode;
  },
  set(value) {
    if (editingAsset.value) {
      editingAsset.value.SiteCode = value;
    } else {
      newAsset.value.SiteCode = value;
    }
  }
});


// --- End Computed Properties ---


// Fetch all assets
const fetchAssets = async () => {
  loading.value = true;
  error.value = null;
  selectedAsset.value = null; // Deselect any selected asset when refreshing
  try {
    const response = await axios.get(assetsApiUrl);
    assets.value = response.data;
  } catch (err) {
    console.error('Error fetching assets:', err);
    error.value = 'Failed to load assets. Please try again later.';
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Fetch locations for the dropdown
const fetchLocations = async () => {
    try {
        const response = await axios.get(locationsApiUrl);
        // Assuming location API returns data like [{ loc_no: 'L001', loc_desc: 'Location A', ... }]
        locations.value = response.data.map(loc => ({
            loc_no: loc.loc_no, // Use loc_no as the value
            loc_desc: loc.loc_desc // Use loc_desc as the display text
        }));
        if (locations.value.length === 0) {
            console.warn('No locations fetched.');
        }
    } catch (err) {
        console.error('Error fetching locations:', err);
        showNotification('Could not load locations list for dropdown.', 'warning');
    }
};

// Fetch departments for the dropdown
const fetchDepartments = async () => {
    try {
        const response = await axios.get(departmentsApiUrl);
        // Assuming department API returns data like [{ dep_no: 'D001', dep_desc: 'Department A', ... }]
        departments.value = response.data.map(dep => ({
            dep_no: dep.dep_no, // Use dep_no as the value
            dep_desc: dep.dep_desc // Use dep_desc as the display text
        }));
        if (departments.value.length === 0) {
            console.warn('No departments fetched.');
        }
    } catch (err) {
        console.error('Error fetching departments:', err);
        showNotification('Could not load departments list for dropdown.', 'warning');
    }
};

// Fetch sites for the dropdown
const fetchSites = async () => {
    try {
        const response = await axios.get(sitesApiUrl);
        // Assuming site API returns data like [{ SiteCode: 'S001', Sitename: 'Site A', ... }]
        sites.value = response.data.map(site => ({
            SiteCode: site.SiteCode, // Use SiteCode as the value
            Sitename: site.Sitename // Use Sitename as the display text
        }));
        if (sites.value.length === 0) {
            console.warn('No sites fetched.');
        }
    } catch (err) {
        console.error('Error fetching sites:', err);
        showNotification('Could not load sites list for dropdown.', 'warning');
    }
};


// Add a new asset
const createAsset = async () => {
  // Basic validation for required fields
  if (!newAsset.value.asset_no.trim() || !newAsset.value.asset_desc.trim() || !newAsset.value.loc_no || !newAsset.value.dep_no || !newAsset.value.SiteCode) {
    showNotification('Asset Number, Description, Location, Department, and Site are required.', 'warning');
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const response = await axios.post(assetsApiUrl, newAsset.value);
    assets.value.push(response.data);
    // Clear form - include all fields
    newAsset.value = {
      asset_no: '', asset_desc: '', loc_no: '', dep_no: '', category: '', status: '',
      criticality: '', auth_emp: '', Tech_notes: '', manf: '', model_no: '', serial_no: '',
      contractor: '', waranty: false, waranty_expire_date: null, waranty_notes: '', asset_notes: '',
      supplier: '', pur_price: null, date_aquired: null, current_value: null, Date_disposed: null,
      Next_Sch: null, bom_no: '', SiteCode: ''
    };
    showNotification('Asset created successfully!', 'success');
  } catch (err) {
    console.error('Error creating asset:', err);
    if (err.response?.status === 409) {
      error.value = err.response.data || 'Asset Number already exists.';
    } else if (err.response?.status === 400) {
       error.value = err.response.data || 'Bad Request: Invalid Location, Department, or Site Code.';
    }
    else {
      error.value = 'Failed to create asset.';
    }
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Select an asset from the table
const selectAsset = (asset) => {
  selectedAsset.value = asset;
   // If you select an asset while editing, stop editing
    if (editingAsset.value) {
        cancelEditing();
    }
    error.value = null; // Clear previous errors
}

// Start editing
const startEditing = () => {
  if (selectedAsset.value) {
      editingAsset.value = { ...selectedAsset.value };
      // When starting edit, clear newAsset form and any errors
      newAsset.value = {
        asset_no: '', asset_desc: '', loc_no: '', dep_no: '', category: '', status: '',
        criticality: '', auth_emp: '', Tech_notes: '', manf: '', model_no: '', serial_no: '',
        contractor: '', waranty: false, waranty_expire_date: null, waranty_notes: '', asset_notes: '',
        supplier: '', pur_price: null, date_aquired: null, current_value: null, Date_disposed: null,
        Next_Sch: null, bom_no: '', SiteCode: ''
      };
      error.value = null;
  } else {
       showNotification('Please select an asset to edit.', 'warning');
  }
};

// Cancel editing
const cancelEditing = () => {
  editingAsset.value = null;
  // When canceling edit, clear newAsset form and any errors
  newAsset.value = {
    asset_no: '', asset_desc: '', loc_no: '', dep_no: '', category: '', status: '',
    criticality: '', auth_emp: '', Tech_notes: '', manf: '', model_no: '', serial_no: '',
    contractor: '', waranty: false, waranty_expire_date: null, waranty_notes: '', asset_notes: '',
    supplier: '', pur_price: null, date_aquired: null, current_value: null, Date_disposed: null,
    Next_Sch: null, bom_no: '', SiteCode: ''
  };
  error.value = null;
};

// Update asset
const updateAsset = async () => {
  // Use editingAsset.value directly as the form is bound to it when editing
   if (!editingAsset.value.asset_desc.trim() || !editingAsset.value.loc_no || !editingAsset.value.dep_no || !editingAsset.value.SiteCode) {
    showNotification('Asset Description, Location, Department, and Site are required.', 'warning');
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    // PUT request uses asset_no in the URL
    const response = await axios.put(`${assetsApiUrl}/${editingAsset.value.asset_no}`, editingAsset.value);

    const index = assets.value.findIndex(a => a.asset_no === editingAsset.value.asset_no);
    if (index !== -1) {
      // Update the asset in the list with the data from the response
      assets.value[index] = { ...assets.value[index], ...response.data };
    }
     // If the updated asset was selected, keep it selected with updated data
     if (selectedAsset.value && selectedAsset.value.asset_no === editingAsset.value.asset_no) {
         selectedAsset.value = { ...selectedAsset.value, ...response.data };
     }

    editingAsset.value = null; // Exit editing mode
    showNotification('Asset updated successfully!', 'success');
  } catch (err) {
    console.error('Error updating asset:', err);
    if (err.response?.status === 404) {
      error.value = 'Asset not found.';
    } else if (err.response?.status === 400) {
        error.value = err.response.data || 'Bad Request: Invalid Location, Department, or Site Code during update.';
    }
    else {
      error.value = 'Failed to update asset.';
    }
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Delete asset
const deleteAsset = async () => {
  if (!selectedAsset.value) {
       showNotification('Please select an asset to delete.', 'warning');
       return;
  }
  const assetNoToDelete = selectedAsset.value.asset_no;

  if (confirm(`Are you sure you want to delete asset ${assetNoToDelete}?`)) { // Assets are usually end nodes, less likely to have children
    loading.value = true;
    error.value = null;
    try {
      // DELETE request uses asset_no in the URL
      await axios.delete(`${assetsApiUrl}/${assetNoToDelete}`);
      assets.value = assets.value.filter(a => a.asset_no !== assetNoToDelete);
      // If the deleted asset was selected or being edited, clear states
      selectedAsset.value = null;
      if(editingAsset.value && editingAsset.value.asset_no === assetNoToDelete) {
          editingAsset.value = null;
      }
      showNotification('Asset deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting asset:', err);
      if (err.response?.status === 404) {
        error.value = 'Asset not found.';
      } else if (err.response?.status === 409) {
         // Specific error message from the backend about FK conflicts elsewhere
         error.value = err.response.data || 'Conflict: Cannot delete asset because it is referenced by other entities (e.g., maintenance logs). You must delete them first.';
      }
      else {
        error.value = 'Failed to delete asset.';
      }
      showNotification(error.value, 'error');
    } finally {
      loading.value = false;
    }
  }
};

// Show notification helper function
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;
  setTimeout(() => {
    notification.value.show = false;
  }, 5000); // Hide after 5 seconds
};


// Fetch data on mount
onMounted(() => {
    fetchAssets();
    fetchLocations(); // Fetch locations for the dropdown
    fetchDepartments(); // Fetch departments for the dropdown
    fetchSites(); // Fetch sites for the dropdown
});
</script>

<template>
  <div class="sites-page">
    <div class="container">
      <div class="form-panel">
        <h2>{{ editingAsset ? 'Edit Asset' : 'Add New Asset' }}</h2>
        <form @submit.prevent="editingAsset ? updateAsset() : createAsset()">
          <div class="form-sections-container">

            <div class="form-section">
              <h3>Basic Information</h3>
              <div class="form-group">
                <label for="asset_no">Asset Number:</label>
                 <input type="text" id="asset_no" v-model="currentAssetNo" required
                       :disabled="editingAsset !== null || loading">
              </div>
              <div class="form-group">
                <label for="asset_desc">Description:</label>
                 <input type="text" id="asset_desc" v-model="currentAssetDesc" required
                       :disabled="loading">
              </div>
               <div class="form-group">
                <label for="auth_emp">Authorized Employee:</label>
                 <input type="text" id="auth_emp" v-model="currentAuthEmp"
                       :disabled="loading">
              </div>
               <div class="form-group">
                <label for="Tech_notes">Technical Notes:</label>
                 <textarea id="Tech_notes" v-model="currentTechNotes" rows="3"
                       :disabled="loading" class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"></textarea>
              </div>
            </div>

            <div class="form-section">
              <h3>Location & Classification</h3>
              <div class="form-group-pair">
                 <div class="form-group nested-group">
                    <label for="loc_no">Location:</label>
                     <select id="loc_no" v-model="currentLocNo" required :disabled="loading">
                       <option value="" disabled>Select Location</option>
                      <option v-for="location in locations" :key="location.loc_no" :value="location.loc_no">
                          {{ location.loc_desc }} ({{ location.loc_no }})
                      </option>
                       <option value="" v-if="locations.length === 0 && !loading">No locations available</option>
                    </select>
                  </div>
                  <div class="form-group nested-group">
                    <label for="dep_no">Department:</label>
                     <select id="dep_no" v-model="currentDepNo" required :disabled="loading">
                       <option value="" disabled>Select Department</option>
                      <option v-for="department in departments" :key="department.dep_no" :value="department.dep_no">
                          {{ department.dep_desc }} ({{ department.dep_no }})
                      </option>
                       <option value="" v-if="departments.length === 0 && !loading">No departments available</option>
                    </select>
                  </div>
              </div>
              <div class="form-group">
                <label for="site_no">Site:</label>
                 <select id="site_no" v-model="currentSiteCode" required :disabled="loading">
                   <option value="" disabled>Select Site</option>
                  <option v-for="site in sites" :key="site.SiteCode" :value="site.SiteCode">
                      {{ site.Sitename }} ({{ site.SiteCode }})
                  </option>
                   <option value="" v-if="sites.length === 0 && !loading">No sites available</option>
                </select>
              </div>
              <div class="form-group-pair">
                 <div class="form-group nested-group">
                    <label for="category">Category:</label>
                     <input type="text" id="category" v-model="currentCategory"
                           :disabled="loading">
                  </div>
                   <div class="form-group nested-group">
                    <label for="status">Status:</label>
                     <input type="text" id="status" v-model="currentStatus"
                           :disabled="loading">
                  </div>
              </div>
              <div class="form-group">
                <label for="criticality">Criticality:</label>
                 <input type="text" id="criticality" v-model="currentCriticality"
                       :disabled="loading">
              </div>
            </div>

            <div class="form-section">
              <h3>Manufacturer & Warranty</h3>
               <div class="form-group-pair">
                 <div class="form-group nested-group">
                    <label for="manf">Manufacturer:</label>
                     <input type="text" id="manf" v-model="currentManf"
                           :disabled="loading">
                  </div>
                   <div class="form-group nested-group">
                    <label for="model_no">Model Number:</label>
                     <input type="text" id="model_no" v-model="currentModelNo"
                           :disabled="loading">
                  </div>
              </div>
              <div class="form-group">
                <label for="serial_no">Serial Number:</label>
                 <input type="text" id="serial_no" v-model="currentSerialNo"
                       :disabled="loading">
              </div>
               <div class="form-group-pair">
                 <div class="form-group nested-group">
                    <label for="contractor">Contractor:</label>
                     <input type="text" id="contractor" v-model="currentContractor"
                           :disabled="loading">
                  </div>
                   <div class="form-group nested-group">
                    <label for="supplier">Supplier:</label>
                     <input type="text" id="supplier" v-model="currentSupplier"
                           :disabled="loading">
                  </div>
              </div>
              <div class="form-group-pair">
                 <div class="form-group nested-group checkbox-group"> <label for="waranty">Warranty:</label>
                     <input type="checkbox" id="waranty" v-model="currentWaranty"
                           :disabled="loading">
                  </div>
                   <div class="form-group nested-group">
                    <label for="waranty_expire_date">Warranty Expire Date:</label>
                     <input type="date" id="waranty_expire_date" v-model="currentWarantyExpireDate"
                           :disabled="loading">
                  </div>
              </div>
              <div class="form-group">
                <label for="waranty_notes">Warranty Notes:</label>
                 <textarea id="waranty_notes" v-model="currentWarantyNotes" rows="2"
                       :disabled="loading" class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"></textarea>
              </div>
            </div>

            <div class="form-section">
              <h3>Financial & Maintenance</h3>
               <div class="form-group-pair">
                 <div class="form-group nested-group">
                    <label for="pur_price">Purchase Price:</label>
                     <input type="number" id="pur_price" v-model="currentPurPrice" step="0.01"
                           :disabled="loading">
                  </div>
                   <div class="form-group nested-group">
                    <label for="current_value">Current Value:</label>
                     <input type="number" id="current_value" v-model="currentCurrentValue" step="0.01"
                           :disabled="loading">
                  </div>
              </div>
              <div class="form-group-pair">
                 <div class="form-group nested-group">
                    <label for="date_aquired">Date Acquired:</label>
                     <input type="date" id="date_aquired" v-model="currentDateAquired"
                           :disabled="loading">
                  </div>
                   <div class="form-group nested-group">
                    <label for="Date_disposed">Date Disposed:</label>
                     <input type="date" id="Date_disposed" v-model="currentDateDisposed"
                           :disabled="loading">
                  </div>
              </div>
              <div class="form-group-pair">
                 <div class="form-group nested-group">
                    <label for="Next_Sch">Next Sch:</label>
                     <input type="date" id="Next_Sch" v-model="currentNextSch"
                           :disabled="loading">
                  </div>
                   <div class="form-group nested-group">
                    <label for="bom_no">BOM No:</label>
                     <input type="text" id="bom_no" v-model="currentBomNo"
                           :disabled="loading">
                  </div>
              </div>
               <div class="form-group">
                <label for="asset_notes">Asset Notes:</label>
                 <textarea id="asset_notes" v-model="currentAssetNotes" rows="3"
                       :disabled="loading" class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"></textarea>
              </div>
            </div>

          </div> <div class="form-actions">
            <button type="submit" class="save-button" :disabled="loading">
              {{ editingAsset ? 'Update Asset' : 'Save Asset' }}
            </button>
            <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingAsset" :disabled="loading">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div class="table-panel">
        <h2>Asset List</h2>

         <div v-if="loading && assets.length === 0">Loading assets...</div>
         <div v-if="error && assets.length === 0" class="error-message">{{ error }}</div>

        <div class="table-actions">
           <button @click="addNew" :disabled="loading">Add New</button>
           <button @click="startEditing" :disabled="!selectedAsset || loading">Edit</button>
           <button @click="deleteAsset" :disabled="!selectedAsset || loading">Delete</button>
           <button @click="fetchAssets" :disabled="loading">Refresh</button>
         </div>

        <table class="site-table">
          <thead>
            <tr>
              <th>Asset No</th>
              <th>Description</th>
              <th>Loc No</th>
              <th>Dept No</th>
              <th>Site Code</th>
              <th>Category</th>
              <th>Status</th>
              <th>Criticality</th>
              <th>Auth Emp</th>
              <th>Tech Notes</th>
              <th>Manufacturer</th>
              <th>Model No</th>
              <th>Serial No</th>
              <th>Contractor</th>
              <th>Warranty</th>
              <th>Warranty Expire Date</th>
              <th>Warranty Notes</th>
              <th>Asset Notes</th>
              <th>Supplier</th>
              <th>Purchase Price</th>
              <th>Date Acquired</th>
              <th>Current Value</th>
              <th>Date Disposed</th>
              <th>Next Sch</th>
              <th>BOM No</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="asset in assets" :key="asset.asset_no"
                :class="{ 'selected': selectedAsset && selectedAsset.asset_no === asset.asset_no }"
                @click="selectAsset(asset)">
              <td>{{ asset.asset_no }}</td>
              <td>{{ asset.asset_desc }}</td>
              <td>{{ asset.loc_no }}</td>
              <td>{{ asset.dep_no }}</td>
              <td>{{ asset.SiteCode }}</td>
              <td>{{ asset.category }}</td>
              <td>{{ asset.status }}</td>
              <td>{{ asset.criticality }}</td>
              <td>{{ asset.auth_emp }}</td>
              <td>{{ asset.Tech_notes }}</td>
              <td>{{ asset.manf }}</td>
              <td>{{ asset.model_no }}</td>
              <td>{{ asset.serial_no }}</td>
              <td>{{ asset.contractor }}</td>
              <td>{{ asset.waranty ? 'Yes' : 'No' }}</td>
              <td>{{ asset.waranty_expire_date ? new Date(asset.waranty_expire_date).toLocaleDateString() : '' }}</td>
              <td>{{ asset.waranty_notes }}</td>
              <td>{{ asset.asset_notes }}</td>
              <td>{{ asset.supplier }}</td>
              <td>{{ asset.pur_price }}</td>
              <td>{{ asset.date_aquired ? new Date(asset.date_aquired).toLocaleDateString() : '' }}</td>
              <td>{{ asset.current_value }}</td>
              <td>{{ asset.Date_disposed ? new Date(asset.Date_disposed).toLocaleDateString() : '' }}</td>
              <td>{{ asset.Next_Sch ? new Date(asset.Next_Sch).toLocaleDateString() : '' }}</td>
              <td>{{ asset.bom_no }}</td>
            </tr>
             <tr v-if="assets.length === 0 && !loading">
                <td colspan="25" style="text-align: center;">No assets found.</td>
             </tr>
          </tbody>
        </table>
      </div>
    </div>

     <div v-if="notification.show" :class="['notification', notification.type]">
      {{ notification.message }}.
    </div>

  </div>
</template>

<style scoped>
/* Copying and adapting styles for the new layout */
.sites-page {
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f4f6f8;
}

.container {
  display: flex;
  flex-direction: column; /* Stack children vertically */
  gap: 20px;
  /* Removed max-width and margin: 0 auto to allow full width */
}

.form-panel {
  width: 100%; /* Form panel takes full width */
  background-color: white;
  padding: 15px; /* Slightly reduced padding */
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Ensure panels have a minimum width */
}

.table-panel {
  width: 100%; /* Table panel takes full width */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Ensure panels have a minimum width */
  overflow-x: auto; /* Keep horizontal scroll for the table */
}

/* Container for the four form sections */
.form-sections-container {
    display: grid;
    grid-template-columns: 1fr 1fr; /* Two columns of sections */
    gap: 20px; /* Gap between the main section columns */
}

/* Style for each individual form section */
.form-section {
    border: 1px solid #ddd; /* Add a border to visually separate sections */
    padding: 15px;
    border-radius: 8px;
    background-color: #f9f9f9; /* Slightly different background for sections */
}

.form-section h3 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #555;
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
}


/* Style for individual form groups within a section */
.form-section .form-group {
    display: grid; /* Use grid for label and input alignment */
    grid-template-columns: 120px 1fr; /* Fixed width for label, rest for input */
    gap: 10px; /* Gap between label and input */
    align-items: center; /* Vertically align label and input */
    margin-bottom: 8px; /* Margin below each form group */
}

/* Style for grouping related form elements side-by-side within a section */
.form-group-pair {
    display: grid;
    grid-template-columns: 1fr 1fr; /* Two columns within the pair */
    gap: 10px; /* Gap between elements in the pair */
    margin-bottom: 8px; /* Match margin of single form-group */
}

/* Ensure nested form groups within a pair use the same alignment grid */
.form-group-pair .nested-group {
    margin-bottom: 0;
    display: grid; /* Use grid for label and input alignment */
    grid-template-columns: 120px 1fr; /* Fixed width for label, rest for input */
    gap: 10px; /* Gap between label and input */
    align-items: center; /* Vertically align label and input */
}

/* Special alignment for checkbox group */
.form-group-pair .nested-group.checkbox-group {
     grid-template-columns: 120px auto; /* Adjust grid for checkbox */
}

.form-group-pair .nested-group.checkbox-group input[type="checkbox"] {
    margin-top: 0; /* Reset margin top */
}


/* Adjust input width within the grid context */
.form-section input[type="text"],
.form-section input[type="number"],
.form-section select,
.form-section input[type="date"],
.form-section textarea {
    /* Removed max-width: 300px; */ /* Let the grid column control width */
    width: 100%; /* Ensure input fills its grid cell */
    padding: 6px 8px; /* Reduced vertical padding, kept horizontal */
    font-size: 0.9rem; /* Slightly smaller font size */
}

/* Adjust checkbox width and alignment */
.form-section input[type="checkbox"] {
    width: auto;
    /* Removed margin-top and margin-left as alignment is handled by grid */
    margin-top: 0;
    margin-left: 0;
    padding: 0; /* No padding on checkbox */
    max-width: none; /* Remove max-width for checkbox */
}

/* Adjust label padding for alignment */
.form-section label {
    padding-right: 10px; /* Add some padding to the right of labels */
    text-align: right; /* Align labels to the right */
}


h2 {
  color: #333;
  margin-bottom: 15px; /* Slightly reduced margin below heading */
  border-bottom: 1px solid #eee;
  padding-bottom: 8px; /* Slightly reduced padding below heading */
}

/* General input styling (kept for consistency, but overridden by .form-section inputs) */
input[type="text"],
input[type="number"],
select,
input[type="date"],
input[type="checkbox"],
textarea
{
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus,
input[type="date"]:focus,
textarea:focus
{
  border-color: #007bff;
  outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled,
input[type="date"]:disabled,
input[type="checkbox"]:disabled,
textarea:disabled
{
  background-color: #e9ecef;
  cursor: not-allowed;
}

.form-actions {
  margin-top: 20px; /* Increased margin top to separate from sections */
  grid-column: 1 / -1; /* Span across all columns in the grid */
  text-align: center;
}

.save-button,
.cancel-button {
  padding: 8px 12px; /* Slightly reduced padding */
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem; /* Slightly smaller font size */
  transition: background-color 0.3s ease;
}

.save-button {
  background-color: #28a745; /* Green */
  color: white;
}

.save-button:hover:not(:disabled) {
  background-color: #218838;
}

.cancel-button {
  background-color: #6c757d; /* Gray */
  color: white;
  margin-left: 10px; /* Added margin between buttons */
}

.cancel-button:hover:not(:disabled) {
  background-color: #5a6268;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}


.table-actions {
  margin-bottom: 15px;
  display: flex;
  gap: 10px; /* Space between buttons */
  flex-wrap: wrap;
}

.table-actions button {
  padding: 8px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background-color: #007bff; /* Blue */
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease, opacity 0.3s ease;
}

/* Specific button styles from SitesView */
.table-actions button:nth-child(2) { /* Edit button */
  background-color: #ffc107; /* Yellow */
  border-color: #ffc107;
  color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
  background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
  background-color: #dc3545; /* Red */
  border-color: #dc3545;
  color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
  background-color: #c82333;
}


.table-actions button:hover:not(:disabled) {
  background-color: #0056b3;
  border-color: #0056b3;
}


.site-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 0.9rem;
}

/* Increased padding for bigger cells */
.site-table th,
.site-table td {
  padding: 10px; /* Slightly reduced cell padding */
  border: 1px solid #ddd;
  text-align: left;
  vertical-align: top; /* Align cell content to top */
}

.site-table th {
  background-color: #e9ecef; /* Light gray */
  font-weight: bold;
  color: #495057;
}

.site-table tbody tr:hover {
  background-color: #f8f9fa; /* Lighter gray on hover */
  cursor: pointer;
}

.site-table tbody tr.selected {
  background-color: #cfe2ff; /* Light blue when selected */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
  }

  /* Stack form sections on small screens */
  .form-sections-container {
      grid-template-columns: 1fr; /* Single column of sections */
      gap: 15px; /* Adjust gap between stacked sections */
  }

  /* Stack form groups within sections on small screens */
  .form-section .form-group,
  .form-group-pair .nested-group {
      grid-template-columns: 1fr; /* Stack labels and inputs vertically */
      gap: 5px; /* Adjust gap */
      align-items: flex-start; /* Align items to the start */
  }

  /* Stack form group pairs on small screens */
  .form-group-pair {
      grid-template-columns: 1fr; /* Stack nested groups vertically */
      gap: 0; /* Remove gap */
  }

  /* Add back margin to nested groups when stacked */
  .form-group-pair .nested-group {
      margin-bottom: 8px; /* Match single form-group margin */
  }

  /* Ensure labels are full width on small screens when stacked */
  .form-section label {
      text-align: left; /* Align labels to the left on small screens */
      padding-right: 0; /* Remove right padding */
      width: 100%; /* Ensure label takes full width */
  }

  /* Ensure inputs take full width on small screens when stacked */
  .form-section input[type="text"],
  .form-section input[type="number"],
  .form-section select,
  .form-section input[type="date"],
  .form-section textarea {
      max-width: none; /* Remove max-width on small screens */
      width: 100%; /* Take full width */
  }

    /* Adjust checkbox alignment on small screens */
    .form-section input[type="checkbox"] {
        margin-left: 0;
    }


  .form-panel,
  .table-panel {
    margin-bottom: 20px;
    min-width: unset;
    flex: unset;
    width: 100%;
  }

  .table-actions {
    flex-direction: column;
    gap: 5px;
  }

  .table-actions button {
    width: 100%;
    margin-right: 0;
  }
}

/* Notification Styling */
.notification {
  position: fixed;
  bottom: 20px;
  left: 20px;
  padding: 15px 20px;
  border-radius: 4px;
  color: white;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: opacity 0.5s ease-in-out;
}

.notification.success {
  background-color: #28a745; /* Green */
}

.notification.error {
  background-color: #dc3545; /* Red */
}

.notification.warning {
  background-color: #ffc107; /* Yellow */
  color: #212529;
}

.error-message {
  color: #dc3545;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #dc3545;
  background-color: #fff3f3;
  border-radius: 4px;
}

</style>
